import React, { useEffect, useState, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import SetupGuard from '../components/SetupGuard';
import { useAuth } from '../services/authContext';

const TYPES = ['assignment', 'test', 'project', 'coursework'];

const emptyForm = {
  classId: '', moduleId: '', title: '', instructions: '', type: 'assignment',
  maxMarks: 100, dueDate: '', allowResubmit: true,
  allowedTypes: 'pdf,docx,doc,zip,jpg,png', maxFileSizeMB: 10,
};

const AssignmentTasks = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';
  const [tasks, setTasks]             = useState([]);
  const [classes, setClasses]         = useState([]);
  const [modules, setModules]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [isModalOpen, setModal]       = useState(false);
  const [current, setCurrent]         = useState(null);
  const [form, setForm]               = useState(emptyForm);
  const [submissions, setSubmissions] = useState(null);
  const [gradeForm, setGradeForm]     = useState({ marksObtained: '', feedback: '' });
  const [gradingId, setGradingId]     = useState(null);

  const load = async () => {
    try {
      setLoading(true); setError('');
      const [t, c, m] = await Promise.all([
        apiService.getAll('assignments'),
        apiService.getAll('classes'),
        apiService.getAll('modules'),
      ]);
      setTasks(t || []); setClasses(c || []); setModules(m || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    tasks.filter(t => [t.title, t.className, t.moduleName || t.subjectName, t.type].join(' ').toLowerCase().includes(search.toLowerCase())),
    [tasks, search]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const openEdit = task => {
    setCurrent(task);
    setForm({
      classId: task.classId, moduleId: task.moduleId || task.subjectId || '',
      title: task.title, instructions: task.instructions || '',
      type: task.type, maxMarks: task.maxMarks,
      dueDate: task.dueDate?.slice(0, 16) || '',
      allowResubmit: task.allowResubmit,
      allowedTypes: task.allowedTypes, maxFileSizeMB: task.maxFileSizeMB,
    });
    setModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true); setError('');
      if (current) {
        await apiService.update('assignments', current.id, form);
      } else {
        await apiService.create('assignments', form);
      }
      toast(current ? 'Assignment updated.' : 'Assignment created and students notified.', 'success');
      setModal(false); setCurrent(null); setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
      toast(err.response?.data?.message || 'Failed to save assignment.', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this assignment?')) return;
    try {
      await apiService.delete('assignments', id);
      await load();
      toast('Assignment deleted.', 'success');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete.');
      toast(err.response?.data?.message || 'Failed to delete.', 'error');
    }
  };

  const viewSubmissions = async task => {
    try {
      const data = await apiService.get(`assignments/${task.id}/submissions`);
      setSubmissions({ task, list: Array.isArray(data) ? data : [] });
    } catch { setSubmissions({ task, list: [] }); }
  };

  const handleGrade = async subId => {
    try {
      await apiService.put(`assignments/submissions/${subId}/grade`, gradeForm);
      toast('Grade saved and student notified.', 'success');
      setGradingId(null);
      setGradeForm({ marksObtained: '', feedback: '' });
      if (submissions) {
        const data = await apiService.get(`assignments/${submissions.task.id}/submissions`);
        setSubmissions(p => ({ ...p, list: Array.isArray(data) ? data : [] }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to grade.');
      toast(err.response?.data?.message || 'Failed to save grade.', 'error');
    }
  };

  const statusBadge = status => {
    const colors = { submitted: '#1976D2', late: '#F57C00', graded: '#388E3C', returned: '#7B1FA2' };
    return <span style={{ background: `${colors[status] || '#666'}22`, color: colors[status] || '#666', padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600 }}>{status}</span>;
  };

  const tableContent = (
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          <tr><th>Title</th><th>Class</th><th>Type</th><th>Due Date</th><th>Max Marks</th><th>Submissions</th><th>Status</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={8} className="no-data-message">No assignments found.</td></tr>
          ) : filtered.map(t => (
            <tr key={t.id}>
              <td><strong>{t.title}</strong>{(t.moduleName || t.subjectName) && <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{t.moduleName || t.subjectName}</div>}</td>
              <td>{t.className}</td>
              <td>{t.type}</td>
              <td style={{ color: new Date(t.dueDate) < new Date() ? '#c62828' : 'inherit' }}>
                {new Date(t.dueDate).toLocaleString()}
              </td>
              <td>{t.maxMarks}</td>
              <td><button className="btn-icon" onClick={() => viewSubmissions(t)}>{t.submissionCount} submitted</button></td>
              <td>{statusBadge(t.status)}</td>
              <td>
                <div className="action-buttons">
                  <button className="btn-icon" onClick={() => openEdit(t)}>Edit</button>
                  <button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleDelete(t.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <LoadingSpinner message="Loading assignments..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Assignments</h1>
        <button className="btn btn-primary" onClick={() => { setCurrent(null); setForm(emptyForm); setModal(true); }}>+ Create Assignment</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isAdmin ? (
        <SetupGuard checks={[
          {
            ok: classes.length > 0,
            icon: '🏫',
            label: 'No Classes exist yet',
            description: 'Create a class or batch before creating assignments.',
            action: { label: 'Create Class', to: '/programmes' },
          },
          {
            ok: modules.length > 0,
            icon: '📚',
            label: 'No Modules exist yet',
            description: 'Create modules before creating assignments.',
            action: { label: 'Create Module', to: '/modules' },
          },
        ]}>
          <div className="search-container">
            <input type="text" placeholder="Search assignments..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {tableContent}
        </SetupGuard>
      ) : (
        <>
          <div className="search-container">
            <input type="text" placeholder="Search assignments..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {tableContent}
        </>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setModal(false)} title={current ? 'Edit Assignment' : 'Create Assignment'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-group">
              <label>Class *</label>
              <select name="classId" value={form.classId} onChange={handleChange} required disabled={!!current}>
                <option value="">Select Class</option>
                {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Module</label>
              <select name="moduleId" value={form.moduleId} onChange={handleChange}>
                <option value="">Select Module</option>
                {modules.map(m => <option key={m._id} value={m._id}>{m.code} - {m.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input name="title" value={form.title} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Instructions</label>
            <textarea name="instructions" value={form.instructions} onChange={handleChange} rows={4} style={{ resize: 'vertical' }} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select name="type" value={form.type} onChange={handleChange}>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Max Marks</label>
              <input type="number" name="maxMarks" value={form.maxMarks} onChange={handleChange} min={1} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Due Date *</label>
              <input type="datetime-local" name="dueDate" value={form.dueDate} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Max File Size (MB)</label>
              <input type="number" name="maxFileSizeMB" value={form.maxFileSizeMB} onChange={handleChange} min={1} max={50} />
            </div>
          </div>
          <div className="form-group">
            <label>Allowed File Types (comma separated)</label>
            <input name="allowedTypes" value={form.allowedTypes} onChange={handleChange} placeholder="pdf,docx,zip,jpg" />
          </div>
          <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" name="allowResubmit" id="resubmit" checked={form.allowResubmit} onChange={handleChange} />
            <label htmlFor="resubmit" style={{ marginBottom: 0 }}>Allow resubmission before deadline</label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : current ? 'Update' : 'Create & Notify Students'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!submissions} onClose={() => { setSubmissions(null); setGradingId(null); }} title={`Submissions — ${submissions?.task?.title}`}>
        {submissions && (
          submissions.list.length === 0 ? (
            <div className="no-data-message">No submissions yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {submissions.list.map(sub => (
                <div key={sub.id} style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <strong>{sub.studentName}</strong>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
                        Submitted: {new Date(sub.submittedAt).toLocaleString()}
                      </div>
                      {sub.notes && <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>{sub.notes}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {statusBadge(sub.status)}
                      {sub.marksObtained !== null && <span style={{ fontWeight: 600 }}>{sub.marksObtained} / {submissions.task.maxMarks}</span>}
                      {sub.downloadUrl && (
                        <button className="btn-icon" onClick={async () => {
                          try {
                            const res = await apiService.raw.get(`assignments/submissions/${sub.id}/download`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a'); a.href = url;
                            a.setAttribute('download', sub.originalName || 'submission');
                            document.body.appendChild(a); a.click(); a.remove();
                            window.URL.revokeObjectURL(url);
                          } catch { toast('Download failed.', 'error'); }
                        }}>⬇ File</button>
                      )}
                      <button className="btn-icon" style={{ color: '#1976D2' }} onClick={() => { setGradingId(sub.id); setGradeForm({ marksObtained: sub.marksObtained || '', feedback: sub.feedback || '' }); }}>
                        Grade
                      </button>
                    </div>
                  </div>
                  {sub.feedback && <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', background: 'var(--bg-secondary)', padding: '0.5rem', borderRadius: 4 }}>Feedback: {sub.feedback}</div>}
                  {gradingId === sub.id && (
                    <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Marks (out of {submissions.task.maxMarks})</label>
                          <input type="number" value={gradeForm.marksObtained} min={0} max={submissions.task.maxMarks}
                            onChange={e => setGradeForm(p => ({ ...p, marksObtained: e.target.value }))} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Feedback</label>
                        <textarea rows={3} value={gradeForm.feedback}
                          onChange={e => setGradeForm(p => ({ ...p, feedback: e.target.value }))} style={{ resize: 'vertical' }} />
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-primary" onClick={() => handleGrade(sub.id)}>Save Grade</button>
                        <button className="btn btn-secondary" onClick={() => setGradingId(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </Modal>
    </div>
  );
};

export default AssignmentTasks;
